import { useMemo } from 'react';
import { cocuklar, haritala, blokunuBul, SANTIYE_GENELI } from '@/lib/konum';

// Üç adımlı konum seçici: (1) Şantiye geneli / Etap / Blok-Bölge,
// (2) seçili blokun içi: katlar (her biri "kat geneli" + o kattaki bölümler)
// ve katsız bölümler, (3) seçili bölümün mahalleri (salon, banyo…; yalnız
// bölümde mahal tanımlıysa görünür). Ağacın her seviyesi seçilebilir — yalnız
// blok seçmek o bloğun ortak alanı, yalnız kat seçmek o katın geneli, yalnız
// bölüm seçmek bölümün tamamı.
//
// Yerel <select>: blokta yüzlerce bölüm olabiliyor; telefonda yerel seçici
// kaydırılabilir ve harfle atlanabilir. Saf props bileşeni — taşeronun
// girişsiz formu `lib/api.js`'i import etmeden kullanabiliyor.
const sinif = 'w-full h-12 px-3 rounded-md border border-[#d0c4b0] bg-white text-base text-[#1a1916] disabled:opacity-60';
const kompaktSinif = 'w-full h-10 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm text-[#1a1916] disabled:opacity-60 disabled:bg-[#faf7f2]';

// adim: masaüstü tablosu her adımı ayrı sütunda çizer — 1: blok/etap,
// 2: blokun içi (+ varsa mahal, altında). Verilmezse üçü birlikte (mobil).
export default function KonumSecici({ konumlar, value, onChange, disabled, yol, testId, adim, kompakt = false }) {
  const harita = useMemo(() => haritala(konumlar), [konumlar]);
  if (!konumlar || konumlar.length === 0) return null;   // yapısı tanımsız şantiye

  const secili = value ? harita[value] : null;
  // Mahal seçiliyse ikinci adımda onun bölümü görünür
  const bolum = secili?.tur === 'mahal' ? harita[secili.parent_id] : (secili?.tur === 'bolum' ? secili : null);
  const mahaller = bolum ? cocuklar(konumlar, bolum.id, 'mahal') : [];
  // Kayıtlı konum artık listede yok (pasifleşmiş): sessizce "Şantiye geneli"
  // gösterilirse taşeron fark etmeden konumu değiştirmiş olurdu.
  const kayip = !!value && !secili;
  const blok = secili ? blokunuBul(secili.id, harita) : null;
  const blokIci = secili && ['kat', 'bolum', 'mahal'].includes(secili.tur);
  const ustDeger = kayip ? value : (blokIci ? blok?.id || '' : (value || ''));
  const katlar = blok ? cocuklar(konumlar, blok.id, 'kat') : [];
  const katsizBolumler = blok ? cocuklar(konumlar, blok.id, 'bolum') : [];
  const icerikVar = katlar.length > 0 || katsizBolumler.length > 0;

  const cls = kompakt ? kompaktSinif : sinif;
  const ust = (
      <select value={ustDeger} disabled={disabled} className={cls} aria-label="Konum 1"
        data-testid={adim === 1 ? testId : undefined}
        onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{SANTIYE_GENELI}</option>
        {kayip && <option value={value} disabled>{yol || 'Konum'} (kaldırıldı — yenisini seçin)</option>}
        {cocuklar(konumlar, null, 'blok').map((b) => (
          <option key={b.id} value={b.id}>{b.ad}</option>
        ))}
        {cocuklar(konumlar, null, 'etap').map((e) => (
          <optgroup key={e.id} label={e.ad}>
            <option value={e.id}>{e.ad} — etap geneli</option>
            {cocuklar(konumlar, e.id, 'blok').map((b) => (
              <option key={b.id} value={b.id}>{b.ad}</option>
            ))}
          </optgroup>
        ))}
      </select>
  );
  const ic = blok && icerikVar && (
        <select value={blokIci ? (bolum?.id || value) : ''} disabled={disabled} className={cls} aria-label="Konum 2"
          data-testid={adim === 2 ? testId : undefined}
          onChange={(e) => onChange(e.target.value || blok.id)}>
          <option value="">{blok.ad} — ortak alan / genel</option>
          {katsizBolumler.map((b) => (
            <option key={b.id} value={b.id}>{b.ad}</option>
          ))}
          {katlar.map((k) => (
            <optgroup key={k.id} label={k.ad}>
              <option value={k.id}>{k.ad} — kat geneli</option>
              {/* Kat adı seçeneğin içinde: kapalı kutuda yalnız seçenek metni
                  görünür, dubleks daire (aynı "Daire 4" üç katta) ayırt edilemiyordu */}
              {cocuklar(konumlar, k.id, 'bolum').map((b) => (
                <option key={b.id} value={b.id}>{b.ad} · {k.ad}</option>
              ))}
            </optgroup>
          ))}
        </select>
  );
  const mahal = bolum && mahaller.length > 0 && (
        <select value={secili?.tur === 'mahal' ? value : ''} disabled={disabled} className={`${cls} ${adim ? '' : 'sm:col-span-2'}`}
          onChange={(e) => onChange(e.target.value || bolum.id)} aria-label="Mahal" data-testid={testId && `${testId}-mahal`}>
          <option value="">{bolum.ad} — tamamı (mahal seçin)</option>
          {mahaller.map((m) => (
            <option key={m.id} value={m.id}>{m.ad}</option>
          ))}
        </select>
  );

  if (adim === 1) return ust;
  if (adim === 2) {
    if (!ic && !mahal) {
      return (
        <select disabled className={cls} aria-label="Konum 2" data-testid={testId}>
          <option>{blok ? '—' : 'Önce Konum 1'}</option>
        </select>
      );
    }
    return <div className="space-y-1.5">{ic}{mahal}</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={testId}>
      {ust}{ic}{mahal}
    </div>
  );
}
